import React, { useRef, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowLeft, Github, Linkedin, Mail, MapPin, Calendar, Award, Users, Brain, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import { Footer2 } from '../components/Footer2';

const TeamMember = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true, threshold: 0.1 });
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedExperience, setSelectedExperience] = React.useState(null);

  // Scroll to top when component mounts or memberId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [memberId]);

  // Team member data - this should match the data structure from Team.jsx
  const teamMembers = {
    'anand-lo': {
      id: 'anand-lo',
      name: "Anand Lo",
      role: "President",
      image: `${process.env.PUBLIC_URL}/AnandLo_Anand Lo.jpeg`,
      year: "Computer Science",
      department: "Executive Leadership",
      bio: "Leading DMLS towards innovative AI research, industry partnerships, and fostering a collaborative learning environment for all members.",
      fullBio: "I’m an Applied Computer Science student at Dalhousie with a minor in Statistics. My current research topic includes oceanography and biosecurity to prevent species invasions; rare neurodegenerative disease; and stress and anxiety from wearable data. I like to bring machine learning, community, and impact together.",
      skills: ["Foundation models", "Retrieval systems", "Spatiotemporal modeling", "Multimodal representation learning", "Uncertainty quantification"],
      achievements: [
        "Proud of what the Dalhousie Machine Learning Society is today."
      ],
      education: "Bachelor of Computer Science, Dalhousie University",
      location: "Halifax, Nova Scotia",
      joinedDate: "November 2024",
      social: {
        linkedin: "https://www.linkedin.com/in/anandlo/",
        discord: "sparkle",
        email: "anandlo@dal.ca"
      },
      workExperience: [
        {
          title: "Research Assistant",
          description: " Oceanography + BioSec- ML + Biosignals- ML + Neuroimaging",
          tech: []
        },
        {
          title: "Data Analyst Intern",
          description: "Prototyping LLM systems for retrieval, semantic mapping, and reasoning across heterogeneous enterprise data, building Python pipelines with automated quality checks and lineage reporting, and drafting an executive paper on AI at TD covering adoption, platform architecture, and engineering best practices.",
          tech: []
        },
        {
          title: "Junior Machine Learning Engineer",
          description: "Worked on adapting Meta’s SAM 2 for aerial-image segmentation by training with mask supervision, running ablations over point- and box-prompting, coupling YOLOv8 detections to generate prompt priors, adding automated diagnostics and learning-rate scheduling to stabilize training, and validating improvements with IoU and targeted visual reviews on occlusions and boundary edges.",
          tech: []
        }

      ]
    },
    'het-jivani': {
      id: 'het-jivani',
      name: "Het Jivani",
      role: "Internal Vice President",
      image: `${process.env.PUBLIC_URL}/Het_Jivani_digital_photo.jpeg`,
      year: "Computer Science",
      department: "Executive Leadership",
      bio: "Managing internal operations, member engagement, and ensuring smooth coordination of society activities and initiatives.",
      fullBio: "Hi I am Het, and I am a computer science student with minor in statistics, my current research lies in analyzing rare neurodegenerative disease. I have worked as data engineer, student researcher, software developer in my previous roles. I like to connect with people interested in AI/ML and collaborate on initiatives involving technology, and really interested in working towards building community. ",
      skills: ["Python", "Machine learning", "Software Development", "Leadership", "Problem solving"],
      achievements: [
        "Vice President Dalhousie Machine Learning Society", "Computing council of canada", "Apprentice @ LSI  RBC borealis."
      ],
      education: "Bachelor of Computer Science, Dalhousie University",
      location: "Halifax, Nova Scotia",
      joinedDate: "September 2024",
      social: {
        linkedin: "https://www.linkedin.com/in/hetjivani/",
        discordUsername: "het",
        email: "Het.Jivani@dal.ca"
      },
      workExperience: [
        {
          title: "Student Researcher",
          description: "Worked on analyzing degeneration of rare neurological disease, and modulating patient's trajectory.",
          tech: []
        },
          {
          title: "Application Developer",
          description: "Worked on UI migration, resolved production level bug, deployed code change to production and lead enterprise level integration in collaboration with vendor, and security team.",
          tech: []
        }
      ]
    },
    'kai-patel': {
      id: 'kai-patel',
      name: "Kai Patel",
      role: "External Vice President",
      image: `${process.env.PUBLIC_URL}/KP.png`,
      year: "Data Science",
      department: "Executive Leadership",
      bio: "As a highly driven and hard-working individual, I am deeply committed to continuous learning, growth, and problem-solving in all aspects of my life. My fascination with machine learning stems from its inherent multidisciplinary nature - a field with applications spanning nearly every domain. I'm empowered by the fact that working in machine learning allows me to explore and meaningfully impact across a diverse range of areas, of course all within the bounds of my technical abilities and domain understanding. I hold a BA in Economics from Western University and am currently pursuing Computer Science at Dalhousie University. Beyond academics, I work as a Junior Machine Learning Engineer at a sports betting startup, where I'm leading the implementation of a market recommendation system using collaborative filtering (unsupervised learning). My involvement in the Dalhousie Machine Learning Society (DMLS) has further deepened my passion for the field, and I'm grateful to be surrounded by such a brilliant community who constantly challenge me to learn and grow each day.",
      fullBio: "Kai Patel is the External Vice President of DMLS, specializing in building strategic partnerships and expanding the society's industry connections. With a strong background in data science and exceptional networking skills, Kai has been successful in establishing collaborations with leading tech companies and representing DMLS at various conferences and industry events.",
      skills: ["Python", "Machine Learning", "Problem Solving", "Leadership", "Resilience"],
      achievements: [
        "Architected, implemented and led the development of WagerApp - a functional real-time gaming platform enabling friends to make tournament-based wagers on various in-person and online activities",
        "Designed and developed the Card Ranking and Generation pipeline for Titan Solutions (a sports betting startup) - establishing the foundation for my future development of an unsupervised learning system powering personalized market recommendations",
        "Founding member and leader of the Dalhousie Machine Learning Society (DMLS) - led technical workshops in machine learning, and fostered collaborations that allowed students to explore ML/AI in a neutral setting",
        "Deadlifted 405lbs 1RM at 16 years old"
      ],
      education: "Bachelor of Data Science, Dalhousie University",
      location: "Halifax, Nova Scotia",
      joinedDate: "December 2024",
      social: {
        linkedin: "https://www.linkedin.com/in/kai-patel-035537210",
        discordUsername: "kp42",
        email: "kaipatel32310@gmail.com"
      },
      workExperience: [
        {
          title: "Machine Learning (ML) Engineer @ Titan Solutions Inc.",
          description: "Leading AI R&D and implementation for next-gen product capabilities. Working on LLM-powered automation and data-driven systems to enhance product intelligence and internal tooling.",
          tech: []
        },
        {
          title: "Lead Software Developer & Co-Founder @ WagerApp",
          description: "Led team of four software developers in building a real-time wagering app targeting Twitch live streamers. Architected and implemented backend/frontend. Headed development of several core app components, including: ▪ Implementing secure authentication/authorization system using stateless JWTs and CSRF tokens, adhering to the Naïve Double Submit Cookie Pattern. ▪ Implementing real-time user communication using WebSockets (Socket.IO), integrated with Redux middleware and a Redis Pub/Sub pattern to facilitate synchronous client updates across multiple channels. Implementing frontend design mock-ups using React (TypeScript)",
          tech: []
        },
        {
          title: "Financial Intern, Associate @ WCB NS",
          description: " • Headed investigative project analyzing business performance metrics (KPIs) in SQL logic, and identified misaligned KPI use across departments; generated report to align understanding between project managers, business intelligence analysts, and financial leaders. • Collaborated with financial analysts to produce financial reports including General Ledger (GL) reconciliations, year-end budget accruals, and quarterly reports for department cost centers. • Automated GL reconciliations process using Power Automate Flow and Excel Office Scripts, saving hours of work for financial analysts. • Developed proof-of-concept proposal for streamlining credit card payment processing using Guidewire API and Selenium WebDriver. • Processed cheques and credit card payments, validated direct deposit enrollments, and filed account payable invoices on daily basis. • Managed accounts receivable operations as student intern for two consecutive summers and hired as part-time financial associate for 2025 winter semester.",
          tech: []
        }
      ]
    },
    
    'shuvro-pal': {
      id: 'shuvro-pal',
      name: "Shuvro Pal",
      role: "Events coordinator",
      image: `${process.env.PUBLIC_URL}/headshot_Shuvro Pal.png`,
      initials: "SP",
      year: "Software Engineering",
      department: "Technical Programs & Development",
      bio: "Shuvro is a PhD student in Computer Science at Dalhousie University, Canada. His research focuses on intelligent and autonomous systems, combining areas such as autonomous vehicles, wireless communication, reinforcement learning, and generative AI. Shuvro’s work aims to design adaptive, cooperative, and edge-intelligent frameworks that enable real-time decision-making and collaboration among connected vehicles. Beyond his research, he is passionate about developing ethical and privacy-conscious AI solutions and mentoring the next generation of data scientists and AI practitioners.",
      fullBio: "Shuvro Pal leads the technical research initiatives at DMLS, focusing on cutting-edge topics in AI and machine learning. With a strong background in software engineering and a passion for innovation, Shuvro drives research projects that explore the intersection of technology and society.",
      skills: ["Machine Learning Research", "Android app development"],
      achievements: [
        "Best paper award for the track",
         "“Deep Learning” at ICICV-2021", 
         "Udacity-AWS Machine Learning Scholarship Program-2021", 
         "Udacity-Facebook Deep Learning Scholarship Nanodegree-2019", 
         "Best mobile article writer of October, 2015 (CodeProject)."
      ],
      education: "Bachelor of Software Engineering, Dalhousie University",
      location: "Halifax, Nova Scotia",
      joinedDate: "August 2025",
      social: {
        linkedin: "https://www.linkedin.com/in/shuvropal/",
        discordUsername: "shuvro_pal",
        email: "shuvro@dal.ca",
        portfolio: "#"
      },
      workExperience: [
        {
          title: "Data scientist (markopolo.ai)",
          description: " Responsible for building GenAI and LLM based data scrapping, annotation and business decision pipeline. Integrated Llama 3.1 & OpenAI/Gemini, built AI-driven personalization (email/SMS), developed NLP models (intent, sentiment, topic detection), designed customer segmentation via clustering, deployed ML pipelines on Azure, automated web scraping for trend analysis.",
          tech: []
        },
        {
          title: "Sr. Machine learning engineer(zantrik.com )",
          description: "Responsible for leading the research and development team focused on Computer Vision and NLP. Developed AI-driven solutions for fleet safety, vehicle recognition, and damage segmentation. Built real-time facial landmark detection, accident prevention, and chatbot automation (OpenAI, Pinecone, LangChain). Integrated Amazon Rekognition for facial recognition.",
          tech: []
        },
        {
          title: "Founder and CEO (Foodeye.org)",
          description: "Responsible for carrying out ideas, app and service development, business growth, resource allocation and investment management.",
          tech: []
        },

      ]
    },
    'nafisah-nubah': {
      id: 'nafisah-nubah',
      name: "Nafisah Nubah",
      role: "Lead of Events and Social Media",
      image: `${process.env.PUBLIC_URL}/headshot-nubah_Nafisah Nubah.jpg`,
      initials: "NN",
      year: "Business & Computer Science",
      department: "Marketing & Outreach",
      bio: "Hi! I’m Nubah, a 3rd-year Computer Science student at Dalhousie and the Social Media Coordinator for DMLS. Before joining the team, I worked on the NIAR research project with DMLS, which further deepened my interest in machine learning, and I’m eager to help bring more initiatives like that to Dal students interested in ML. I’m super excited about exploring how machine learning can solve real-world problems and love finding creative ways to share what our team is working on. When I’m not designing posts or helping promote DMLS events, you’ll probably find me involved around campus. If you see me around, come say hi, I’d love to connect!",
      fullBio: "Nafisah Nubah is the Social Media Co-ordinator for DMLS, combining business acumen with technical knowledge to drive brand awareness and expand the society's reach. Nafisah's creative approach to marketing and content creation has significantly increased DMLS's visibility and engagement across multiple platforms.",
      skills: ["Applied ML Research", "Full-Stack Development", "Cloud Technologies", "Teaching & Mentorship", "Community Engagement"],
      achievements: [
        "Sexton Scholar", "Dean’s List", "AWS Cloud Practitioner", "FCS Leadership Team"
      ],
      education: "Bachelor of Business & Computer Science, Dalhousie University",
      location: "Halifax, Nova Scotia",
      joinedDate: "August 2025",
      social: {
        linkedin: "https://www.linkedin.com/in/nafisah-nubah-3a355829b/",
        discord: "nubah123",
        email: "Nafisah.Nubah@dal.ca"
      },
      workExperience: [
        {
          title: "Student Researcher, Dalhousie Machine Learning Society (DMLS)",
          description: "As a Student Researcher with DMLS, I had the opportunity to work under faculty mentorship on applying machine learning techniques to fMRI brain imaging data. Our project focused on identifying potential biomarkers for neurological diseases, and through it, I explored various ML algorithms and model architectures. It was an exciting introduction to the intersection of neuroscience and AI, and it deepened my interest in using ML for meaningful, real-world applications.",
          tech: []
        },
        {
          title: "Software Developer, Dalhousie University (Student Accessibility Centre)",
          description: "At the Student Accessibility Centre, I worked with a team to design and build a Java-based application that helped automate parts of the exam scheduling process. It was a rewarding project where I got to combine problem-solving with user-focused design — improving efficiency for staff while learning how software can directly support accessibility and inclusion on campus. I also collaborated with staff members to brainstorm and implement new workflow automation features.",
          tech: []
        },
        {
          title: "Computer Science Instructor, SuperNOVA at Dalhousie University",
          description: "As a Computer Science Instructor with SuperNOVA, I taught programming concepts to students of all ages — from writing their first lines of Python to experimenting with web development using HTML, CSS, and JavaScript, and even building interactive projects with Arduinos. This was my first co-op, and I also had the chance to lead tech workshops, helping students discover the fun side of coding and grow their confidence through hands-on projects.",
          tech: []
        }
      ]
    },
        'chizulu-zephaniah': {
      id: 'chizulu-zephaniah',
      name: "Chizulu Zephaniah",
      role: "Web Developer",
      image: `${process.env.PUBLIC_URL}/Chizulu.jpg`,
      initials: "CZ",
      year: "Computer Science",
      department: "Technology & Infrastructure",
      bio: "Building and maintaining DMLS digital platforms, websites, and technical infrastructure for the society.",
      fullBio: "Adept Software Engineer with experience developing AI systems, scalable backend architectures, and full-stack applications across research and enterprise environments. Proven expertise across the full stack (Python, Java, JavaScript, React, PostgreSQL) and DevOps pipelines (Azure, Kubernetes, Docker). Specializing in leveraging Generative AI and advanced data analytics to drive business value and hit stakeholder targets.",
      skills: ["Full-Stack Web Development", "UI/UX Design", "DevOps", "React.js", "Node.js", "Database Management", "SQL","Azure","Kubernetes","Docker","Generative AI","Data Analytics"],
      achievements: [
        "Single-handedly redesigned and developed the DMLS website",
        "Developed asynchronous dashboard for member management and content delivery",
        "Optimized website SEO performance",
        "Designed and maintained robust database schema for society content"
      ],
      education: "Bachelor of Computer Science, Dalhousie University",
      location: "Halifax, Nova Scotia",
      joinedDate: "June 2025",
      social: {
        linkedin: "https://www.linkedin.com/in/chizulu-zephaniah-906582308/",
        github: "https://github.com/dev-zeph",
        email: "zephchizulu@gmail.com",
        portfolio: "https://chizuluzephaniah.vercel.app/"
      },
      workExperience: [
        {
          title: "Founder & Software Engineer – Collana",
          description: "Founded and led the development of Collana, a dedicated job application platform for students to earn experience before graduation. Architected and implemented a full-stack web application using React.js for the frontend and Node.js with Express for the backend, ensuring a responsive and user-friendly interface. Designed a scalable PostgreSQL database schema to manage user profiles, job listings, and application tracking, optimizing query performance for high traffic. Integrated third-party APIs for authentication (OAuth 2.0) and email notifications (SendGrid), enhancing user engagement and security. Deployed the application on AWS using Docker containers orchestrated with Kubernetes, ensuring high availability and scalability. Implemented CI/CD pipelines using GitHub Actions to automate testing and deployment processes, reducing release times by 40%.",
          tech: ["React", "Node.js", "PostgreSQL", "AWS", "Docker", "Kubernetes", "GitHub Actions"]
        },
        {
          title: "Software Developer Intern – AN Zeph & Associates",
          description: "•	Led the end-to-end development of a full-stack property management platform using MERN/PERN stack principles, automating critical business workflows and eliminating over 30% of manual data entry. Integrated secure authentication and role-based access control with JWT and Supabase Auth, ensuring data privacy compliance and reliable session handling across multiple devices. Deployed and containerized the system on AWS EC2 using Docker and GitHub Actions CI/CD, enabling zero downtime and scalable performance.",
          tech: ["React", "Node.js", "PostgreSQL", "JWT", "Supabase", "AWS", "Docker"]
        },
        {
          title: "Technical Lead / Director of AR Development – CHEM-AR",
          description: "Architected and directed the development of a high-performance, open-source modular Augmented Reality (AR) visualization engine using Three.js and WebXR, supporting 1000+ active users. Engineered custom, GPU-accelerated rendering pipelines using GLSL shaders and advanced physics-based rendering (PBR) techniques to achieve 60 FPS frame rates and photorealistic molecule visualization. Established robust, enterprise-grade Azure CI/CD pipelines for automated build, testing, and deployment, reducing integration defects by 25% and ensuring seamless cross-platform compatibility.",
          tech: ["Three.js", "WebXR", "GLSL", "Azure", "CI/CD", "PBR"]
        },
        {
          title: "Lead Web Developer – Dalhousie Consulting Association & Dalhousie Machine Learning Society",
          description: "Architected and executed a full-stack migration of two high-traffic society websites from static HTML to a React.js MPA (Multiple Page Application), improving performance and boosting SEO/traffic by over 35%. Designed and implemented a scalable PostgreSQL schema managed by Supabase, leveraging custom API endpoints to power dynamic user dashboards and self-service content management. Integrated an API-driven headless CMS (Strapi) for asynchronous content delivery, reducing internal content update latency by 80%, and aligned the technical roadmap with key stakeholders and budget constraints while establishing the CI/CD pipeline.",
          tech: ["React", "PostgreSQL", "Supabase", "Strapi", "CI/CD", "SEO"]
        },
        
      ]
    },
    'akash-maity': {
      id: 'akash-maity',
      name: "Akash Maity",
      role: "Student Representative",
      image: `${process.env.PUBLIC_URL}/Akash.jpg`,
      year: "Computer Science",
      department: "Student Relations",
      bio: "Third-year Computer Science student with a strong interest in AI, automation, and software development. Bridging communication between students and leadership team while helping organize engaging AI-focused events.",
      fullBio: "I am a third-year Computer Science student at Dalhousie University with a strong interest in AI, automation, and software development. I enjoy building intelligent and efficient systems that improve productivity and user experience. My experience ranges from creating Android applications and Chrome extensions to developing AI automation workflows using n8n. I have also made and am currently working on several interactive projects based on machine learning and AI, focusing on real-world applications that enhance accessibility and innovation. As a Student Representative at DMLS, I aim to bridge communication between students and the leadership team while helping organize engaging AI-focused events and initiatives.",
      skills: ["Generative AI & Prompt Engineering", "AI Workflow Automation (n8n)", "Full-Stack & API Integration", "Android Development", "Innovative Problem-Solving using Java and Python"],
      achievements: [
        "Google Cloud Certified in Introduction to Generative AI",
        "Google Cloud Certified in Introduction to Large Language Models",
        "Google Cloud Certified in Introduction to Responsible AI",
        "Google Cloud Certified in Responsible AI: Applying AI Principles",
        "Agentic AI Certificate from ShiftKey Labs",
        "Teaching Assistant at Dalhousie University"
      ],
      education: "Bachelor of Computer Science, Dalhousie University",
      location: "Halifax, Nova Scotia",
      joinedDate: "November 2025",
      social: {
        linkedin: "https://www.linkedin.com/in/akash-maity-8888a22b3",
        github: "https://github.com/mike000522",
        email: "ak298902@dal.ca"
      },
      funFacts: [
        "Love building creative AI tools",
        "Can code for hours when inspired",
        "Enjoy exploring new coffee spots in Halifax",
        "Big fan of tech meetups and hackathons",
        "Always curious about space and astronomy",
        "A chef too"
      ],
      workExperience: [
        {
          title: "Student Personal Assistant (SPA) – AI Automation Platform",
          description: "Developed an AI-based student assistant using n8n that automates emails, schedules, and budget tracking. Integrated workflow automation for real-time academic support and personalized recommendations, improving productivity for students.",
          tech: []
        },
        {
          title: "QuickCash – Android App",
          description: "Built an Android app for posting and finding short-term jobs, integrating Google Maps and PayPal API. Designed a simple interface and streamlined local job postings to help users find quick work opportunities.",
          tech: []
        },
        {
          title: "Chat Application – Java",
          description: "Created a client-server chat system in Java enabling real-time two-way communication. Implemented multithreading and socket programming to support simultaneous message sending and receiving.",
          tech: []
        }
      ]
    },
    'sophie-eruokwu': {
      id: 'sophie-eruokwu',
      name: "Sophie Eruokwu",
      role: "Mentorship Lead",
      image: `${process.env.PUBLIC_URL}/Sophie.jpg`,
      year: "Computer Science",
      department: "Mentorship & Community",
      bio: "Third-year Computer Science student focusing on AI, Cybersecurity, and FinTech. Passionate about supporting the community and turning everyday problems into practical coded solutions.",
      fullBio: "Hi! I'm Sophie, a third-year Computer Science student at Dal, focusing on AI, Cybersecurity, and FinTech, with an ongoing interest in quantum computing. I've created projects ranging from AI/ML prototypes and bootstrapping tools, to games and web apps, and I'm active in student-led initiatives, like the Women in Tech society, ShiftKey Labs, and the Dal Machine Learning Society. I'm passionate about supporting my community and enjoy turning everyday problems into practical coded solutions (for fun, of course). I'm always open to connecting and collaborating with others to bring impactful ideas to life.",
      skills: ["Python", "Java", "Front-end development (React)", "Creative problem-solving", "Team collaboration"],
      achievements: [
        "Cognizant African NS and African Descent Leadership Scholarship recipient",
        "Dream Legacy Foundation's Innovation Trailblazer Award recipient",
        "3x Sexton Scholar recipient",
        "1x Dean's List recipient"
      ],
      education: "Bachelor of Computer Science, Dalhousie University",
      location: "Halifax, Nova Scotia",
      joinedDate: "November 2025",
      social: {
        linkedin: "https://www.linkedin.com/in/sophie-e-b66a1a228/",
        github: "https://github.com/chrissy9730",
        email: "sp332391@dal.ca"
      },
      funFacts: [
        "I'm a published author with two released works",
        "I'm an avid gamer, especially within the RPG genre",
        "I don't enjoy cooking, but I do love baking",
        "I don't do it often, but I occasionally draw and watercolor paint",
        "I once broke a 100m dash track record in my province the day before my birthday"
      ],
      workExperience: [
        {
          title: "ShiftKey Student Ambassador",
          description: "Help coordinate activities for programs to connect students with pathways in AI, data analysis, software development, network security, and more, demonstrating strong organizational and communication skills. Collaborate with the ShiftKey team to plan student engagement events, represent ShiftKey at info sessions, support data-driven program planning, and maintain comprehensive documentation.",
          tech: []
        },
        {
          title: "Junior Programmer",
          description: "Developed features for a live service game with 10M+ active users using C# and .NET frameworks, collaborating with internal teams to design application features. Utilized Jenkins for automated builds and testing, troubleshooting software issues while maintaining documentation using Confluence and Jira in an Agile development environment.",
          tech: []
        },
        {
          title: "TECH Hub Leacross Outreach Member",
          description: "Facilitated outreach events, like game jams and presentations, engaging 50+ P-12 students, including young women and underrepresented groups, to spark interest in computer science and programming languages. Represented the Faculty of Computer Science in outreach programs, connecting with diverse student groups and promoting inclusivity in technology education.",
          tech: []
        }
      ]
    },
    'rafat-hossain': {
      id: 'rafat-hossain',
      name: "Rafat Hossain",
      role: "Treasurer",
      image: `${process.env.PUBLIC_URL}/Rafat.jpg`,
      year: "Computer Science",
      department: "Finance",
      bio: "Treasurer for DMLS, managing financial operations. Volunteering as an ML Researcher on a project focused on understanding glacier melting and its links to climate change.",
      fullBio: "Hi! I am Rafat, the treasurer for the Dalhousie Machine Learning Society. Excited to collaborate with fellow students and researchers passionate about machine learning! I am currently volunteering as a Machine Learning Researcher on a project focused on understanding the melting of glaciers and its links to climate change. I apply machine learning techniques to analyze satellite data, model glacier dynamics, and uncover patterns that contribute to predicting future ice loss. This work connects technology and environmental science, helping generate insights that can inform global sustainability efforts.",
      skills: ["Photography", "Full-stack Web Dev", "Programming (Python, JavaScript, Java)", "Image and Video editing", "Data Analysis & Visualization"],
      achievements: [
        "Dean's List at Dalhousie University FCS",
        "AWS Certified Cloud Practitioner"
      ],
      education: "Bachelor of Computer Science, Dalhousie University",
      location: "Halifax, Nova Scotia",
      joinedDate: "November 2025",
      social: {
        linkedin: "https://www.linkedin.com/in/rafat--hossain/",
        github: "https://github.com/dirter.rh",
        email: "mh836316@dal.ca"
      },
      funFacts: [
        "I love photography",
        "I'm a big cinephile",
        "I have a cat named Mango",
        "My favorite author is Albert Camus"
      ],
      workExperience: [
        {
          title: "Volunteer Researcher",
          description: "Volunteering as a Machine Learning Researcher on a project focused on understanding the melting of glaciers and its links to climate change. I apply machine learning techniques to analyze satellite data, model glacier dynamics, and uncover patterns that contribute to predicting future ice loss. This work connects technology and environmental science, helping generate insights that can inform global sustainability efforts.",
          tech: []
        }
      ]
    },

    
  };

  const member = teamMembers[memberId];

  if (!member) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-black mb-4">Member Not Found</h1>
          <p className="text-muted-foreground mb-8">The team member you're looking for doesn't exist.</p>
          <Link 
            to="/team"
            className="bg-[#0395fc] text-white px-6 py-3 font-bold border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200"
          >
            Back to Team
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-16 sm:pt-24 pb-8 sm:pb-16 bg-cover bg-center bg-no-repeat relative overflow-hidden" style={{ backgroundImage: 'url(/6.jpg)' }}>
        <div className="absolute inset-0 bg-black/50 z-0"></div>
        
        {/* Back Button */}
        <div className="container mx-auto px-4 relative z-10">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            onClick={() => navigate('/team')}
            className="flex items-center gap-2 text-white hover:text-yellow-300 transition-colors mb-8 font-bold"
          >
            <ArrowLeft className="size-5" />
            Back to Team
          </motion.button>
        </div>

        {/* Animated background elements */}
        <motion.div 
          className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-2xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left - Member Info */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={isHeroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-white text-center lg:text-left"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-white/20 text-white px-3 py-1.5 lg:px-4 lg:py-2 rounded-full border border-white/30 text-xs lg:text-sm font-bold mb-4 lg:mb-6 backdrop-blur-sm"
              >
                <Award className="size-3 lg:size-4" />
                {member.department}
              </motion.div>

              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black mb-3 lg:mb-4 leading-tight"
              >
                {member.name}
              </motion.h1>

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-lg sm:text-xl lg:text-2xl text-yellow-300 font-bold mb-4 lg:mb-6"
              >
                {member.role}
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="flex flex-col sm:flex-row items-center lg:items-start gap-3 lg:gap-6 mb-6 lg:mb-8 text-white/90 text-sm lg:text-base"
              >
                <div className="flex items-center gap-2">
                  <MapPin className="size-4" />
                  <span>{member.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="size-4" />
                  <span>Joined {member.joinedDate}</span>
                </div>
              </motion.div>

              {/* Member Image - Mobile Only */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="lg:hidden mb-6"
              >
                <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-full overflow-hidden border-4 border-white mx-auto">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `${process.env.PUBLIC_URL}/logo192.png`;
                    }}
                  />
                </div>
              </motion.div>

              {/* Social Links */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.9 }}
                className="flex justify-center lg:justify-start gap-3 lg:gap-4"
              >
                <motion.a 
                  href={member.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="bg-white/20 backdrop-blur-sm p-2.5 lg:p-3 rounded-full hover:bg-white hover:text-[#0395fc] transition-colors border border-white/30"
                >
                  <Linkedin className="size-4 lg:size-5" />
                </motion.a>
                {member.id === 'chizulu-zephaniah' && member.social.github && member.social.github !== "#" ? (
                  <motion.a 
                    href={member.social.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="bg-white/20 backdrop-blur-sm p-2.5 lg:p-3 rounded-full hover:bg-white hover:text-[#0395fc] transition-colors border border-white/30"
                  >
                    <Github className="size-4 lg:size-5" />
                  </motion.a>
                ) : null}
                <motion.a 
                  href={`mailto:${member.social.email}`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="bg-white/20 backdrop-blur-sm p-2.5 lg:p-3 rounded-full hover:bg-white hover:text-[#0395fc] transition-colors border border-white/30"
                >
                  <Mail className="size-4 lg:size-5" />
                </motion.a>
              </motion.div>
            </motion.div>

            {/* Right - Large Avatar */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={isHeroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:flex justify-center lg:justify-end"
            >
              <div className="relative">
                            {/* Right - Member Image */}
            <div className="w-32 h-32 sm:w-48 sm:h-48 lg:w-64 lg:h-64 rounded-full overflow-hidden border-4 border-white shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] group-hover:scale-110 transition-transform duration-300">
              <img
                src={member.image}
                alt={member.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `${process.env.PUBLIC_URL}/logo192.png`; // Fallback image
                }}
              />
            </div>
                {/* Floating decoration */}
                <motion.div
                  className="absolute -top-3 -right-3 lg:-top-4 lg:-right-4 w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 xl:w-16 xl:h-16 bg-yellow-300 rounded-full flex items-center justify-center border-2 lg:border-4 border-white shadow-lg"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <Award className="size-5 sm:size-6 lg:size-7 xl:size-8 text-[#0395fc]" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Biography Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-12 lg:mb-16"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black mb-6 lg:mb-8 text-center">About {member.name.split(' ')[0]}</h2>
            <div className="bg-gray-50 p-4 sm:p-6 lg:p-8 border-2 lg:border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] lg:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                {member.fullBio}
              </p>
            </div>
          </motion.div>

          {/* Skills & Achievements Grid */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-12 lg:mb-16">
            {/* Skills */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl sm:text-2xl font-black mb-4 lg:mb-6 flex items-center gap-2">
                <Brain className="size-5 lg:size-6 text-[#0395fc]" />
                Skills & Expertise
              </h3>
              <div className="flex flex-wrap gap-2 lg:gap-3">
                {member.skills.map((skill, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-[#0395fc] text-white px-3 py-1.5 lg:px-4 lg:py-2 text-sm font-bold border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] transition-all duration-200"
                  >
                    {skill}
                  </motion.span>
                ))}
              </div>
            </motion.div>

            {/* Achievements */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl sm:text-2xl font-black mb-4 lg:mb-6 flex items-center gap-2">
                <Award className="size-5 lg:size-6 text-[#0395fc]" />
                Key Achievements
              </h3>
              <div className="space-y-2 lg:space-y-3">
                {member.achievements.map((achievement, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-2 lg:gap-3 p-2 lg:p-3 bg-gray-50 border-l-4 border-[#0395fc]"
                  >
                    <Zap className="size-4 lg:size-5 text-[#0395fc] mt-0.5 flex-shrink-0" />
                    <span className="text-sm lg:text-base text-muted-foreground">{achievement}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Work Experience Section */}
          {member.workExperience && member.workExperience.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl sm:text-3xl font-black mb-6 lg:mb-8 text-center flex items-center justify-center gap-2">
                <Users className="size-6 lg:size-8 text-[#0395fc]" />
                Work Experience
              </h3>
              <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {member.workExperience.map((experience, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                    className="bg-white border-2 lg:border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] lg:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(3,149,252,0.3)] lg:hover:shadow-[12px_12px_0px_0px_rgba(3,149,252,0.3)] transition-all duration-300 p-4 lg:p-6"
                  >
                    <h4 className="text-lg lg:text-xl font-black mb-2 lg:mb-3 text-[#0395fc]">{experience.title}</h4>
                    <p className="text-sm lg:text-base text-muted-foreground mb-3 lg:mb-4 leading-relaxed line-clamp-3">{experience.description}</p>
                    <div className="flex flex-wrap gap-1.5 lg:gap-2 mb-3">
                      {experience.tech.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border font-bold"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedExperience(experience);
                        setIsModalOpen(true);
                      }}
                      className="text-sm text-[#0395fc] hover:text-[#0070c0] font-bold mt-auto"
                    >
                      Read More →
                    </button>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 lg:py-16 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl sm:text-3xl font-black mb-3 lg:mb-4">I'd love to hear from you!</h3>
            <p className="text-sm lg:text-base text-gray-300 mb-6 lg:mb-8 max-w-2xl mx-auto">
              
            </p>
            <motion.a
              href={member.social.portfolio || member.social.linkedin || member.social.github}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 bg-[#0395fc] hover:bg-[#0395fc]/90 text-white px-6 py-3 lg:px-8 lg:py-4 text-base lg:text-lg font-bold border-2 lg:border-4 border-[#0395fc] shadow-[3px_3px_0px_0px_rgba(255,255,255,0.3)] lg:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] lg:hover:shadow-[6px_6px_0px_0px_rgba(255,255,255,0.3)] transition-all duration-200"
            >
              <Users className="size-4 lg:size-5" />
              Reach out
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Experience Modal */}
      {isModalOpen && selectedExperience && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-xl lg:text-2xl font-black text-[#0395fc]">{selectedExperience.title}</h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <p className="text-base lg:text-lg text-muted-foreground mb-6 leading-relaxed">
                {selectedExperience.description}
              </p>
              
              <div className="flex flex-wrap gap-2">
                {selectedExperience.tech.map((tech, techIndex) => (
                  <span
                    key={techIndex}
                    className="text-sm bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full border font-bold"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <Footer2 />
    </div>
  );
};

export default TeamMember;